import { GetObjectCommand, PutObjectCommand, ListObjectsV2Command, DeleteObjectCommand, CopyObjectCommand } from '@aws-sdk/client-s3';
import { s3Client } from '../config/aws';
import type { Sequence, User, SequenceMedia } from '../types';

const BUCKET_NAME = 'xrmgpublic';
const PUBLIC_PREFIX = 'internal/barker/public/';
const USER_PREFIX = 'internal/barker/userdata/';
const TEMP_PREFIX = 'internal/barker/temp/';

export function createSlugFromName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-')     // Replace spaces with hyphens
    .replace(/-+/g, '-')      // Replace multiple hyphens with single hyphen
    .trim();
}

export function generateS3Key(sequence: Sequence, user?: User): string {
  const nameSlug = sequence.name.trim() 
    ? createSlugFromName(sequence.name)
    : sequence.id;
  
  if (user) {
    return `${USER_PREFIX}${user.cloudFolder}/sequences/${nameSlug}-${sequence.id}.json`;
  }
  return `${PUBLIC_PREFIX}${nameSlug}-${sequence.id}.json`;
}

export async function testS3Connection(): Promise<boolean> {
  console.log('Testing S3 connection with specific prefix...');
  try {
    const command = new ListObjectsV2Command({
      Bucket: BUCKET_NAME,
      Prefix: 'internal/barker/',
      MaxKeys: 1
    });
    
    const response = await s3Client.send(command);
    console.log('S3 connection test response:', response);
    return true;
  } catch (error) {
    console.error('S3 connection test failed:', error);
    return false;
  }
}

export async function findSequenceObjectsByID(sequenceId: string, user?: User): Promise<string[]> {
  console.log(`Finding all S3 objects for sequence ID: ${sequenceId}`);
  
  try {
    const prefix = user ? `${USER_PREFIX}${user.cloudFolder}/sequences/` : PUBLIC_PREFIX;
    const command = new ListObjectsV2Command({
      Bucket: BUCKET_NAME,
      Prefix: prefix,
    });

    const response = await s3Client.send(command);
    
    if (!response.Contents || response.Contents.length === 0) {
      console.log('No objects found in the specified S3 path');
      return [];
    }
    
    const matchingKeys = response.Contents
      .map(obj => obj.Key)
      .filter(key => key && key.includes(sequenceId) && key.endsWith('.json')) as string[];
    
    console.log(`Found ${matchingKeys.length} objects matching sequence ID ${sequenceId}`);
    return matchingKeys;
  } catch (error) {
    console.error('Error finding sequence objects:', error);
    return [];
  }
}

export async function deleteS3Object(key: string): Promise<boolean> {
  console.log(`Deleting S3 object: ${key}`);
  
  try {
    const command = new DeleteObjectCommand({
      Bucket: BUCKET_NAME,
      Key: key
    });

    await s3Client.send(command);
    console.log(`Successfully deleted S3 object: ${key}`);
    return true;
  } catch (error) {
    console.error(`Error deleting S3 object ${key}:`, error);
    return false;
  }
}

export async function fetchPublicSequences(): Promise<Sequence[]> {
  console.log('Fetching public sequences from S3...');
  
  try {
    const connectionSuccessful = await testS3Connection();
    if (!connectionSuccessful) {
      console.error('S3 connection test failed, aborting fetch');
      return [];
    }
    
    const command = new ListObjectsV2Command({
      Bucket: BUCKET_NAME,
      Prefix: PUBLIC_PREFIX,
    });

    const response = await s3Client.send(command);
    
    if (!response.Contents || response.Contents.length === 0) {
      console.log('No objects found in the specified S3 path');
      return [];
    }
    
    const sequences: Sequence[] = [];

    for (const object of response.Contents) {
      if (object.Key && object.Key.endsWith('.json')) {
        try {
//         console.log(`Processing public sequence file: ${object.Key}`);
          const getCommand = new GetObjectCommand({
            Bucket: BUCKET_NAME,
            Key: object.Key
          });
          
          const data = await s3Client.send(getCommand);
          
          if (!data.Body) {
            console.error(`No body returned for object: ${object.Key}`);
            continue;
          }
          
          const sequenceData = await data.Body.transformToString();
          
          try {
            const parsedData = JSON.parse(sequenceData);
            console.log(`Parsed public sequence data for ${object.Key}:`, {
              id: parsedData.id,
              name: parsedData.name,
              mediaCount: parsedData.media?.length || 0,
              mediaUrls: parsedData.media?.map((m: SequenceMedia) => ({
                sentenceIndex: m.sentenceIndex,
                url: m.url,
                cloudPath: m.cloudPath
              }))
            });
            
            // Normalize media URLs to use full S3 URLs
            const normalizedMedia = parsedData.media?.map((media: SequenceMedia) => {
              const fullUrl = media.cloudPath ? `https://${BUCKET_NAME}.s3.amazonaws.com/${media.cloudPath}` : media.url;
              // console.log(`Normalizing public media URL:`, {
              //   sequenceName: parsedData.name,
              //   sentenceIndex: media.sentenceIndex,
              //   originalUrl: media.url,
              //   cloudPath: media.cloudPath,
              //   fullUrl
              // });
              return {
                ...media,
                url: fullUrl
              };
            });
            
            const normalizedSequence = {
              ...parsedData,
              isPublic: true,
              media: normalizedMedia || [],
              settings: {
                voice: parsedData.settings?.voice || 'Joanna',
                rate: parsedData.settings?.rate || 1,
                language: parsedData.settings?.language || 'en-US',
                pitch: parsedData.settings?.pitch || 1,
                engine: parsedData.settings?.engine || 'neural',
                autoplayGifs: parsedData.settings?.autoplayGifs ?? true
              },
              audioCache: parsedData.audioCache || {}
            };
            
            sequences.push(normalizedSequence);
          } catch (parseError) {
            console.error(`Error parsing JSON for ${object.Key}:`, parseError);
          }
        } catch (getError) {
          console.error(`Error fetching object ${object.Key}:`, getError);
        }
      }
    }

    console.log(`Successfully loaded ${sequences.length} public sequences with media:`, 
      sequences.map(seq => ({
        name: seq.name,
        mediaCount: seq.media?.length || 0,
        mediaUrls: seq.media?.map(m => ({
          sentenceIndex: m.sentenceIndex,
          url: m.url,
          cloudPath: m.cloudPath
        }))
      }))
    );

    return sequences;
  } catch (error) {
    console.error('Error fetching public sequences:', error);
    return [];
  }
}

export async function fetchUserSequences(user: User): Promise<Sequence[]> {
  console.log(`Fetching sequences for user: ${user.fullName}`);
  
  try {
    const prefix = `${USER_PREFIX}${user.cloudFolder}/sequences/`;
    const command = new ListObjectsV2Command({
      Bucket: BUCKET_NAME,
      Prefix: prefix,
    });

    const response = await s3Client.send(command);
    
    if (!response.Contents || response.Contents.length === 0) {
      console.log('No sequences found for user');
      return [];
    }
    
    const sequences: Sequence[] = [];

    for (const object of response.Contents) {
      if (object.Key && object.Key.endsWith('.json')) {
        try {
//          console.log(`Processing user sequence file: ${object.Key}`);
          const getCommand = new GetObjectCommand({
            Bucket: BUCKET_NAME,
            Key: object.Key
          });
          
          const data = await s3Client.send(getCommand);
          
          if (!data.Body) continue;
          
          const sequenceData = await data.Body.transformToString();
          const parsedData = JSON.parse(sequenceData);
          
          console.log(`Parsed user sequence data for ${object.Key}:`, {
            id: parsedData.id,
            name: parsedData.name,
            mediaCount: parsedData.media?.length || 0,
            mediaUrls: parsedData.media?.map((m: SequenceMedia) => ({
              sentenceIndex: m.sentenceIndex,
              url: m.url,
              cloudPath: m.cloudPath
            }))
          });

          // Normalize media URLs to use full S3 URLs
          const normalizedMedia = parsedData.media?.map((media: SequenceMedia) => {
            const fullUrl = media.cloudPath ? `https://${BUCKET_NAME}.s3.amazonaws.com/${media.cloudPath}` : media.url;
            // console.log(`Normalizing user media URL:`, {
            //   sequenceName: parsedData.name,
            //   sentenceIndex: media.sentenceIndex,
            //   originalUrl: media.url,
            //   cloudPath: media.cloudPath,
            //   fullUrl
            // });
            return {
              ...media,
              url: fullUrl
            };
          });
          
          sequences.push({
            ...parsedData,
            isPublic: false,
            cloudId: object.Key,
            media: normalizedMedia || [],
            settings: {
              voice: parsedData.settings?.voice || 'Joanna',
              rate: parsedData.settings?.rate || 1,
              language: parsedData.settings?.language || 'en-US',
              pitch: parsedData.settings?.pitch || 1,
              engine: parsedData.settings?.engine || 'neural',
              autoplayGifs: parsedData.settings?.autoplayGifs ?? true
            },
            audioCache: parsedData.audioCache || {}
          });
        } catch (error) {
          console.error(`Error processing sequence: ${object.Key}`, error);
        }
      }
    }

    console.log(`Successfully loaded ${sequences.length} user sequences with media:`, 
      sequences.map(seq => ({
        name: seq.name,
        mediaCount: seq.media?.length || 0,
        mediaUrls: seq.media?.map(m => ({
          sentenceIndex: m.sentenceIndex,
          url: m.url,
          cloudPath: m.cloudPath
        }))
      }))
    );

    return sequences;
  } catch (error) {
    console.error('Error fetching user sequences:', error);
    return [];
  }
}

async function moveMediaToFinalLocation(sequence: Sequence): Promise<Sequence> {
  if (!sequence.media?.length) return sequence;

  console.log(`Moving media files for sequence ${sequence.name}:`, 
    sequence.media.map(m => ({
      sentenceIndex: m.sentenceIndex,
      currentPath: m.cloudPath,
      finalPath: m.finalPath
    }))
  );

  const updatedMedia: SequenceMedia[] = [];
  
  for (const media of sequence.media) {
    // Skip if media is not in temp storage
    if (!media.cloudPath?.startsWith(TEMP_PREFIX) || !media.finalPath) {
      console.log(`Skipping media move (not in temp storage):`, {
        sentenceIndex: media.sentenceIndex,
        cloudPath: media.cloudPath,
        finalPath: media.finalPath
      });
      updatedMedia.push(media);
      continue;
    }

    try {
      console.log(`Moving media from ${media.cloudPath} to ${media.finalPath}`);
      
      // Copy to final location
      const copyCommand = new CopyObjectCommand({
        Bucket: BUCKET_NAME,
        CopySource: `${BUCKET_NAME}/${media.cloudPath}`,
        Key: media.finalPath
      });
      await s3Client.send(copyCommand);
      console.log(`Successfully copied media to ${media.finalPath}`);

      // Delete from temp location
      const deleteCommand = new DeleteObjectCommand({
        Bucket: BUCKET_NAME,
        Key: media.cloudPath
      });
      await s3Client.send(deleteCommand);
      console.log(`Successfully deleted temp media at ${media.cloudPath}`);

      // Update media object with new URL
      const newUrl = `https://${BUCKET_NAME}.s3.amazonaws.com/${media.finalPath}`;
      console.log(`Updating media URL from ${media.url} to ${newUrl}`);
      
      updatedMedia.push({
        ...media,
        url: newUrl,
        cloudPath: media.finalPath
      });
    } catch (error) {
      console.error('Error moving media:', error);
      // Keep original media object if move fails
      updatedMedia.push(media);
    }
  }

  return {
    ...sequence,
    media: updatedMedia
  };
}

export async function saveUserSequence(sequence: Sequence, user: User, oldName?: string): Promise<boolean> {
  console.log(`Saving sequence for user ${user.fullName}: ${sequence.name}`);
  
  try {
    // Move media files to final location first
    const sequenceWithMovedMedia = await moveMediaToFinalLocation(sequence);
    
    const newKey = generateS3Key(sequenceWithMovedMedia, user);
    
    if (oldName && oldName !== sequence.name) {
      const existingKeys = await findSequenceObjectsByID(sequence.id, user);
      const keysToDelete = existingKeys.filter(key => key !== newKey);
      
      for (const key of keysToDelete) {
        await deleteS3Object(key);
      }
    }
    
    const normalizedSequence = {
      id: sequenceWithMovedMedia.id,
      name: sequenceWithMovedMedia.name,
      sentences: sequenceWithMovedMedia.sentences,
      media: sequenceWithMovedMedia.media,
      isPublic: false,
      settings: {
        voice: sequenceWithMovedMedia.settings.voice || 'Joanna',
        rate: sequenceWithMovedMedia.settings.rate || 1,
        language: sequenceWithMovedMedia.settings.language || 'en-US',
        pitch: sequenceWithMovedMedia.settings.pitch || 1,
        engine: sequenceWithMovedMedia.settings.engine || 'neural',
        autoplayGifs: sequenceWithMovedMedia.settings.autoplayGifs ?? true
      },
      audioCache: sequenceWithMovedMedia.audioCache || {}
    };
    
    const command = new PutObjectCommand({
      Bucket: BUCKET_NAME,
      Key: newKey,
      Body: JSON.stringify(normalizedSequence),
      ContentType: 'application/json'
    });

    await s3Client.send(command);
    return true;
  } catch (error) {
    console.error('Error saving user sequence:', error);
    return false;
  }
}

export async function savePublicSequence(sequence: Sequence, oldName?: string): Promise<boolean> {
  console.log(`Saving public sequence: ${sequence.name} (${sequence.id})`);
  
  try {
    // Move media files to final location first
    const sequenceWithMovedMedia = await moveMediaToFinalLocation(sequence);
    
    const newKey = generateS3Key(sequenceWithMovedMedia);
    console.log(`Saving to S3 path: ${newKey}`);
    
    if (oldName && oldName !== sequence.name) {
      const existingKeys = await findSequenceObjectsByID(sequence.id);
      const keysToDelete = existingKeys.filter(key => key !== newKey);
      
      for (const key of keysToDelete) {
        await deleteS3Object(key);
      }
    }
    
    const normalizedSequence = {
      id: sequenceWithMovedMedia.id,
      name: sequenceWithMovedMedia.name,
      sentences: sequenceWithMovedMedia.sentences,
      media: sequenceWithMovedMedia.media,
      isPublic: true,
      settings: {
        voice: sequenceWithMovedMedia.settings.voice || 'Joanna',
        rate: sequenceWithMovedMedia.settings.rate || 1,
        language: sequenceWithMovedMedia.settings.language || 'en-US',
        pitch: sequenceWithMovedMedia.settings.pitch || 1,
        engine: sequenceWithMovedMedia.settings.engine || 'neural',
        autoplayGifs: sequenceWithMovedMedia.settings.autoplayGifs ?? true
      },
      audioCache: sequenceWithMovedMedia.audioCache || {}
    };
    
    const command = new PutObjectCommand({
      Bucket: BUCKET_NAME,
      Key: newKey,
      Body: JSON.stringify(normalizedSequence),
      ContentType: 'application/json'
    });

    await s3Client.send(command);
    return true;
  } catch (error) {
    console.error('Error saving public sequence:', error);
    return false;
  }
}